import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';

const CheckEmailPage: React.FC = () => {
  const [sp] = useSearchParams();
  const presetEmail = sp.get('email') || '';
  const [email, setEmail] = useState(presetEmail);
  const { API_BASE_URL } = useAuth();

  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<string>('');
  const [err, setErr] = useState<string>('');

  const resend = async () => {
    setBusy(true);
    setMsg('');
    setErr('');
    try {
      if (!email) throw new Error('Enter your email to resend the verification link.');
      const r = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j?.error || 'Failed to resend verification email');
      }
      setMsg('Verification email sent. Please check your inbox.');
    } catch (e: any) {
      setErr(e?.message || 'Failed to resend verification email');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto max-w-md px-6 pt-16">
        <header className="text-center">
          <img src="/logo-color.svg" alt="" aria-hidden="true" className="mx-auto h-8 w-auto" />
          <h1 className="display-hero mt-6 text-3xl font-extrabold tracking-tight text-gray-900">
            Check your email
          </h1>
          <p className="mt-2 text-sm text-gray-600">
            We’ve sent a verification link{email ? <> to <strong>{email}</strong></> : ''}.
          </p>
        </header>

        <main id="main" className="mt-8">
          <div className="rounded-2xl border border-gray-200 bg-white p-6 elev-1 space-y-5">
            <div className="space-y-2">
              <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                Your email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="mt-1 block w-full rounded-xl border border-gray-300 bg-white px-3 py-2 text-gray-900 shadow-sm placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-sedra focus:ring-offset-2"
              />
              <p className="text-xs text-gray-500">
                Didn’t get the email? Check spam or resend a new link.
              </p>
            </div>

            {err && (
              <div className="rounded-xl border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                {err}
              </div>
            )}
            {msg && (
              <div className="rounded-xl border border-green-200 bg-green-50 px-3 py-2 text-sm text-green-700">
                {msg}
              </div>
            )}

            <div className="flex flex-col gap-3">
              <button
                type="button"
                onClick={resend}
                disabled={busy || !email}
                className="w-full rounded-xl bg-sedra px-4 py-2.5 text-sm font-semibold text-white transition-colors disabled:cursor-not-allowed disabled:opacity-60 hover:bg-sedra-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
              >
                {busy ? 'Sending…' : 'Resend verification email'}
              </button>
              <a
                href="mailto:"
                className="w-full text-center rounded-xl border border-gray-300 px-4 py-2.5 text-sm font-semibold text-gray-700 transition-colors hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-sedra"
              >
                Open your email app
              </a>
            </div>

            <div className="pt-2 text-center text-sm text-gray-600">
              Wrong email? <Link to="/register" className="text-sedra hover:underline">Create a new account</Link> ·{' '}
              <Link to="/login" className="text-sedra hover:underline">Back to sign in</Link>
            </div>
          </div>

          <div className="mt-6 text-center text-xs text-gray-500">
            Having trouble? Contact support at <a className="underline" href="mailto:support@sedrahq.com">support@sedrahq.com</a>
          </div>
        </main>
      </div>
    </div>
  );
};

export default CheckEmailPage; 